'use client';

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEventSourceEvent } from '@/shared/hooks/useEventSourceEvent';
import { subscriptionKeys } from '../queryKeys';
import { getBalance } from '../actions/getBalance';

/**
 * React Query hook for token balance.
 * Live updates come via SSE `subscription.balance.updated`, whose payload carries
 * the authoritative new balance — we PATCH the cache (setQueryData) instead of
 * invalidating, so a burst of per-block settle events during generation does not
 * fire a refetch POST each time. Only components observing this key re-render.
 */
export function useBalance(familyId: string | null | undefined) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: subscriptionKeys.balance(familyId ?? ''),
    queryFn: () => getBalance(),
    enabled: !!familyId,
    // The wallet is authoritative in ACCOUNTS. Portal SSE only patches this cache
    // for changes the PORTAL itself makes (settles/refunds) — an accounts-side
    // change (admin top-up, a purchase, spend in another app) emits no portal
    // event. Self-heal by re-reading the central balance whenever the user comes
    // back to the tab or the widget remounts, so those changes surface without a
    // manual reload. (Overrides the global staleTime/refetchOnWindowFocus:false.)
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: 'always',
  });

  useEventSourceEvent('subscription.balance.updated', (payload) => {
    if (!familyId || payload.familyId !== familyId) return;
    const next = payload.balance;
    if (typeof next !== 'number') return;
    queryClient.setQueryData<{ balance: number } | undefined>(
      subscriptionKeys.balance(familyId),
      (prev) => (prev ? { ...prev, balance: next } : prev),
    );
  });

  const refresh = useCallback(() => {
    if (familyId) {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.balance(familyId) });
    }
  }, [queryClient, familyId]);

  return {
    balance: data?.balance ?? null,
    loading: isLoading,
    refresh,
  };
}
